import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SubscriberEntitlement,
  SubscriberEntitlementsView,
} from '@azko/shared';

import { ApiRequestError, api } from '../../lib/api.js';
import { useSession } from '../../lib/session.js';
import { Alert, Button, StatusBadge, TextInput, cx } from '../../components/ui.js';

/**
 * Excecao comercial: o que ESTA conta tem de diferente do plano dela.
 *
 * A tela existe para uma frase que a equipe diz ao telefone o tempo todo —
 * "esse mes eu libero 500 encomendas para voces" — e que ate agora so podia ser
 * cumprida com SQL na mao. Sem motivo gravado, sem prazo, e sem ninguem
 * conseguir responder meses depois por que aquela conta tem um numero diferente
 * de todas as outras.
 *
 * O desenho segue a pergunta do atendimento, que nunca e "quanto ele tem" e sim
 * "por que ele tem isso, e ate quando". Por isso cada linha mostra as TRES
 * coisas lado a lado: o que o plano da, o que a excecao muda e o que esta
 * valendo agora — em vez do numero efetivo sozinho, que esconde justamente a
 * parte que alguem vai precisar explicar.
 *
 * Linha vencida continua aparecendo, marcada. Some-la seria fazer o atendente
 * prometer de novo uma cortesia que ele ja deu e o sistema ja tirou.
 */

function valueLabel(
  value: number | null | boolean | undefined,
  kind: SubscriberEntitlement['kind'],
): string {
  if (value === undefined) return kind === 'limit' ? 'nao contratado' : 'desligado';
  if (value === null) return 'ilimitado';
  if (typeof value === 'boolean') return value ? 'ligado' : 'desligado';
  return String(value);
}

export function SubscriberOverrides({ subscriberId }: { subscriberId: string }) {
  const queryClient = useQueryClient();
  const { staff } = useSession();

  const [editing, setEditing] = useState<string | null>(null);
  const [rawValue, setRawValue] = useState('');
  const [unlimited, setUnlimited] = useState(false);
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const podeEditar = staff?.permissions.includes('plataforma:assinantes:editar') ?? false;

  const { data } = useQuery({
    queryKey: ['admin', 'excecoes', subscriberId],
    queryFn: () =>
      api.get<SubscriberEntitlementsView>(`/admin/assinantes/${subscriberId}/excecoes`),
  });

  function close() {
    setEditing(null);
    setRawValue('');
    setUnlimited(false);
    setReason('');
    setExpiresAt('');
    setFailure(null);
  }

  function onError(cause: unknown) {
    setFailure(
      cause instanceof ApiRequestError ? cause.message : 'Nao consegui salvar a excecao.',
    );
  }

  function done() {
    close();
    // A excecao muda o que a conta pode fazer: a tela do assinante e os
    // contadores que dependem de entitlements ficaram velhos junto.
    void queryClient.invalidateQueries({ queryKey: ['admin'] });
  }

  const save = useMutation({
    mutationFn: (item: SubscriberEntitlement) =>
      api.put(`/admin/assinantes/${subscriberId}/excecoes/${item.key}`, {
        value:
          item.kind === 'feature'
            ? rawValue === 'true'
            : unlimited
              ? null
              : Number(rawValue.replace(/\D/g, '') || '0'),
        reason: reason.trim(),
        // `datetime-local` entrega "2026-10-31T12:00" sem fuso; o `Date` do
        // navegador o interpreta no fuso de quem esta na tela, que e o certo —
        // quem digita a data da cortesia esta no mesmo fuso do cliente.
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      }),
    onSuccess: done,
    onError,
  });

  const remove = useMutation({
    mutationFn: (item: SubscriberEntitlement) =>
      api.post(`/admin/assinantes/${subscriberId}/excecoes/${item.key}/remover`, {
        reason: reason.trim(),
      }),
    onSuccess: done,
    onError,
  });

  if (!data) return null;

  const comExcecao = data.items.filter((item) => item.override !== null);
  // Por padrao so o que ja e excecao, porque e a resposta da pergunta que
  // trouxe alguem ate aqui. A lista completa fica a um clique para quem vai
  // CRIAR a primeira — sem ela o atendente teria de saber a chave de cabeca.
  const visiveis = showAll ? data.items : comExcecao;

  function startEdit(item: SubscriberEntitlement) {
    setEditing(item.key);
    setFailure(null);
    setReason(item.override?.reason ?? '');
    setUnlimited(item.override ? item.override.value === null : false);
    setRawValue(
      item.kind === 'feature'
        ? String(item.override?.value ?? item.effectiveValue ?? false)
        : item.override && typeof item.override.value === 'number'
          ? String(item.override.value)
          : '',
    );
    setExpiresAt(
      item.override?.expiresAt
        ? // `datetime-local` nao aceita o "Z" nem os segundos do ISO.
          new Date(item.override.expiresAt).toISOString().slice(0, 16)
        : '',
    );
  }

  return (
    <div className="mt-6 border-t border-line-soft pt-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow">Excecao comercial</p>
          <p className="mt-1 text-sm text-muted">
            {comExcecao.length === 0
              ? `Esta conta segue o plano ${data.planName ?? '—'} em tudo.`
              : `${comExcecao.length} ${comExcecao.length === 1 ? 'chave diferente' : 'chaves diferentes'} do plano ${data.planName ?? '—'}.`}
          </p>
        </div>

        <Button variant="ghost" className="py-1.5" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'So as excecoes' : 'Ver tudo que da para liberar'}
        </Button>
      </div>

      {visiveis.length === 0 && (
        <p className="mt-3 text-sm text-faint">
          Nenhuma excecao. Abra a lista completa para criar a primeira.
        </p>
      )}

      <ul className="mt-4 space-y-2">
        {visiveis.map((item) => {
          const o = item.override;
          const vencida = o?.expired ?? false;

          return (
            <li
              key={item.key}
              className={cx(
                'rounded-2xl border p-3',
                o && !vencida ? 'border-accent/40 bg-accent/5' : 'border-line bg-surface-2',
              )}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold">{item.label}</span>
                  {o && (
                    <StatusBadge tone={vencida ? 'neutral' : 'accent'}>
                      {vencida ? 'excecao vencida' : 'excecao'}
                    </StatusBadge>
                  )}
                </span>

                {podeEditar && (
                  <Button
                    variant="quiet"
                    className="py-1"
                    onClick={() => (editing === item.key ? close() : startEdit(item))}
                  >
                    {editing === item.key ? 'Fechar' : o ? 'Alterar' : 'Criar excecao'}
                  </Button>
                )}
              </div>

              {/* As tres colunas que respondem "por que ele tem isso". */}
              <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <span className="flex gap-1.5">
                  <dt className="text-muted">Plano:</dt>
                  <dd>{valueLabel(item.planValue, item.kind)}</dd>
                </span>
                {o && (
                  <span className="flex gap-1.5">
                    <dt className="text-muted">Excecao:</dt>
                    <dd className={cx(vencida && 'line-through text-faint')}>
                      {valueLabel(o.value, item.kind)}
                    </dd>
                  </span>
                )}
                <span className="flex gap-1.5">
                  <dt className="text-muted">Valendo:</dt>
                  <dd className="font-bold">{valueLabel(item.effectiveValue, item.kind)}</dd>
                </span>
                {item.usage !== null && (
                  <span className="flex gap-1.5">
                    <dt className="text-muted">Usado no mes:</dt>
                    <dd className="tabular-nums">{item.usage}</dd>
                  </span>
                )}
              </dl>

              {o && (
                <p className="mt-1 text-sm text-faint">
                  {o.reason}
                  {o.expiresAt
                    ? ` • ${vencida ? 'venceu' : 'vale ate'} ${new Date(o.expiresAt).toLocaleDateString('pt-BR')}`
                    : ' • sem prazo'}
                </p>
              )}

              {editing === item.key && (
                <div className="mt-3 space-y-3 rounded-xl border border-line bg-surface p-3">
                  {item.kind === 'limit' ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <TextInput
                        value={rawValue}
                        disabled={unlimited}
                        onChange={(event) => setRawValue(event.target.value.replace(/\D/g, ''))}
                        placeholder="Novo limite"
                        inputMode="numeric"
                        aria-label={`Novo limite de ${item.label}`}
                        className="w-40"
                      />
                      <label className="flex items-center gap-2 text-sm text-muted">
                        <input
                          type="checkbox"
                          checked={unlimited}
                          onChange={(event) => setUnlimited(event.target.checked)}
                          className="size-4 accent-accent"
                        />
                        sem limite
                      </label>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {['true', 'false'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setRawValue(option)}
                          className={
                            rawValue === option
                              ? 'rounded-pill border border-accent bg-accent/10 px-3 py-1.5 text-sm font-semibold text-accent'
                              : 'rounded-pill border border-line bg-surface-2 px-3 py-1.5 text-sm font-semibold text-muted'
                          }
                        >
                          {option === 'true' ? 'Ligado' : 'Desligado'}
                        </button>
                      ))}
                    </div>
                  )}

                  <TextInput
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Por que esta conta e excecao (obrigatorio)"
                    aria-label="Motivo da excecao"
                    maxLength={255}
                  />

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted">
                      Vale ate
                    </label>
                    <input
                      type="datetime-local"
                      value={expiresAt}
                      onChange={(event) => setExpiresAt(event.target.value)}
                      className="mt-1 w-full rounded-2xl border border-line bg-surface-2 px-4 py-3 text-fg focus:border-accent/60 focus:outline-none"
                    />
                    {/* Cortesia sem data vira desconto permanente que ninguem
                        lembra de ter dado — dizer isso aqui e mais barato do
                        que descobrir seis meses depois. */}
                    <p className="mt-1 text-xs text-faint">
                      Vazio = permanente. A maioria das cortesias tem prazo.
                    </p>
                  </div>

                  {failure && <Alert>{failure}</Alert>}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      className="py-1.5"
                      loading={save.isPending}
                      disabled={
                        reason.trim().length < 3 ||
                        (item.kind === 'limit' && !unlimited && rawValue === '') ||
                        (item.kind === 'feature' && rawValue === '')
                      }
                      onClick={() => save.mutate(item)}
                    >
                      Salvar excecao
                    </Button>

                    {o && (
                      <Button
                        variant="ghost"
                        className="py-1.5"
                        loading={remove.isPending}
                        disabled={reason.trim().length < 3}
                        onClick={() => remove.mutate(item)}
                      >
                        Remover — volta ao plano
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
